// Geographic center of the United States (Kansas, near Lebanon)
// Used as fallback when no jobs with location data are available
const US_GEOGRAPHIC_CENTER = { lat: 39.8283, lng: -98.5795 };

// Check if Mapbox should be used
function useMapbox() {
  // Check data attribute first (set by view template)
  const mapContainer = document.getElementById('pages-jobs-view-map');
  if (mapContainer && mapContainer.dataset.useMapbox === 'true') {
    return true;
  }
  // Fallback to global config (if set via window)
  return window.mapsConfig?.provider === 'mapbox' || false;
}

// Get the appropriate manager
function getMapsManager() {
  return useMapbox() ? window.mapboxManager : window.googleMapsManager;
}

// Get the API key/access token
function getMapsCredential(container) {
  return useMapbox() 
    ? container.dataset.accessToken 
    : container.dataset.apiKey;
}

document.addEventListener('turbo:load', function(e) {
  const mapContainer = document.getElementById('pages-jobs-view-map');
  if (!mapContainer) return;

  const manager = getMapsManager();
  if (!manager) {
    console.error('Maps Manager not available');
    mapContainer.innerHTML = '<div class="flex items-center justify-center h-full text-gray-500">Maps Manager not loaded</div>';
    return;
  }

  // Check if map is visible (desktop) or hidden (mobile)
  const isMapVisible = isMapContainerVisible(mapContainer);

  if (isMapVisible) {
    // Desktop: Direct loading for always-visible map
    loadJobsMapDirectly(mapContainer);
  } else {
    // Mobile: Lazy loading for hidden map
    loadJobsMapLazily(mapContainer);
  }
});

/* --------------------------------------------------
                Map Visibility Detection
-------------------------------------------------- */
function isMapContainerVisible(container) {
  // Check if container is visible using CSS media queries
  const computedStyle = window.getComputedStyle(container);

  // If display is none or visibility is hidden, it's not visible
  if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
    return false;
  }

  // Check if container has dimensions (mobile might have 0 height)
  const rect = container.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/* --------------------------------------------------
                Direct Map Loading (Desktop)
-------------------------------------------------- */
async function loadJobsMapDirectly(container) {
  try {
    const manager = getMapsManager();
    const credential = getMapsCredential(container);
    await manager.loadAPI(credential);
    createJobsMap(container);
  } catch (error) {
    console.error('Failed to load maps:', error);
    container.innerHTML = '<div class="flex items-center justify-center h-full text-gray-500">Failed to load map service</div>';
  }
}

/* --------------------------------------------------
                Lazy Map Loading (Mobile)
-------------------------------------------------- */
function loadJobsMapLazily(container) {
  // For lazy loading, we'll let createJobsMap handle the map creation
  // when the container becomes visible
  const manager = getMapsManager();
  manager.registerLazyMap(
    container, 
    {}, // Empty options - createJobsMap will generate them
    'jobs-view-map', 
    (map) => {
      // Use the shared createJobsMap function for consistency
      createJobsMap(container);
    }
  );
}

/* --------------------------------------------------
                Helper Functions
-------------------------------------------------- */
function filterJobsWithLocation(jobs) {
  return jobs.filter(job => job.location_lat && job.location_lng);
}

/* --------------------------------------------------
                Calculate Jobs Center
-------------------------------------------------- */
function calculateJobsCenter(jobs) {
  if (jobs.length === 0) {
    return US_GEOGRAPHIC_CENTER;
  }

  // Note: jobs should already be filtered by caller
  // For a single job, center on that job
  if (jobs.length === 1) {
    return {
      lat: parseFloat(jobs[0].location_lat),
      lng: parseFloat(jobs[0].location_lng)
    };
  }

  // For multiple jobs, calculate bounding box center to show all jobs
  let minLat = parseFloat(jobs[0].location_lat);
  let maxLat = parseFloat(jobs[0].location_lat);
  let minLng = parseFloat(jobs[0].location_lng);
  let maxLng = parseFloat(jobs[0].location_lng);

  for (const job of jobs) {
    const lat = parseFloat(job.location_lat);
    const lng = parseFloat(job.location_lng);

    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  }

  // Return center of bounding box
  return {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2
  };
}

/* --------------------------------------------------
                Create Jobs Map (Shared Logic)
-------------------------------------------------- */
function createJobsMap(container) {
  const useMapboxProvider = useMapbox();
  const manager = getMapsManager();
  const credential = getMapsCredential(container);

  // Load jobs data from data attribute
  let jobsData;
  try {
    jobsData = JSON.parse(container.dataset.jobs || '[]');
  } catch (e) {
    console.warn('Failed to parse jobs data');
    return;
  }

  // Filter jobs for markers
  const jobsWithLocation = filterJobsWithLocation(jobsData);
  
  if (jobsWithLocation.length === 0) {
    console.warn('No jobs with location data found');
    return;
  }

  // Calculate center based on job locations for this planroom
  const center = calculateJobsCenter(jobsWithLocation);

  // Find the newest job's updated_at to invalidate cache if needed
  // Use all jobs (not just those with locations) for cache invalidation
  const newestJobUpdatedAt = jobsData
    .map(job => job.updated_at)
    .filter(date => date)
    .sort((a, b) => new Date(b) - new Date(a))[0] || null;

  // Create a stable cache key based on planroom and job count
  const planroomId = container.dataset.planroomId || 'default';
  const jobCount = jobsWithLocation.length;
  const cacheKey = `jobs-map-${planroomId}-${jobCount}-${new Date().toISOString()}`;

  if (useMapboxProvider) {
    createMapboxJobsMap(container, manager, credential, jobsWithLocation, center, cacheKey, newestJobUpdatedAt);
  } else {
    createGoogleJobsMap(container, manager, credential, jobsWithLocation, center, cacheKey, newestJobUpdatedAt);
  }
}

/* --------------------------------------------------
                Create Google Maps Jobs Map
-------------------------------------------------- */
function createGoogleJobsMap(container, manager, apiKey, jobsWithLocation, center, cacheKey, newestJobUpdatedAt) {
  // Use higher zoom for single job, lower zoom for multiple jobs
  const zoom = jobsWithLocation.length === 1 ? 12 : 7;

  const mapOptions = {
    zoom: zoom, 
    mapTypeControl: false,
    streetViewControl: true,
    zoomControl: true,
    zoomControlOptions: {
      position: google.maps.ControlPosition.RIGHT_CENTER
    },
    center: center,
    mapId: 'biddy_jobs_map',
    cacheKey: cacheKey
  };

  const map = manager.createMapWithCache(container, mapOptions, 'jobs-view-map', newestJobUpdatedAt);

  const mapInfoWindow = new google.maps.InfoWindow();
  const mapMarkers = [];

  // Loop through jobs and create markers
  jobsWithLocation.forEach((job, index) => {
    const markerOptions = {
      position: { 
        lat: parseFloat(job.location_lat), 
        lng: parseFloat(job.location_lng)
      },
      map: map,
      title: job.name
    };

    const pinElement = new google.maps.marker.PinElement({
      scale: 1,
      background: '#000000',
      borderColor: '#FFFFFF',
      glyphColor: '#FFFFFF',
      glyph: '●'
    });
    markerOptions.content = pinElement.element;

    // Create the marker
    const mapMarker = new google.maps.marker.AdvancedMarkerElement(markerOptions);
    mapMarkers.push(mapMarker);

    // Handle click events
    const clickHandler = function() {
      mapInfoWindow.setContent(
        '<div class="flex flex-col gap-1">' +
          '<div>' +
            '<div class="text-sm font-semibold">' + job.name + '</div>' +
            '<div class="text-xs text-gray-500 font-medium">' + job.location + '</div>' +
          '</div>' +
          '<a href="/pages/jobs/' + job.id + '/view/show" class="block text-xs font-semibold focus:outline-none">View Project &rarr;</a>' +
        '</div>'
      );
      mapInfoWindow.setOptions({ maxWidth: 240 });
      mapInfoWindow.open(map, this);
    };

    // Add event listeners
    mapMarker.addEventListener('gmp-click', clickHandler);
    mapMarker.addEventListener('mouseenter', clickHandler);

    // Handle mouse enter events
    const jobCard = document.querySelector('[data-js="job-card"][data-index="' + index + '"]');
    if (jobCard) {
      jobCard.addEventListener('mouseenter', (e) => {
        // Pan map to marker location if it's outside the viewport
        const markerPosition = {
          lat: parseFloat(job.location_lat),
          lng: parseFloat(job.location_lng)
        };
        map.panTo(markerPosition);
        
        mapInfoWindow.close();
        mapMarker.dispatchEvent(new Event('gmp-click'));
      });
    }
  });

  // Update the registered map with markers and info window
  const existingMapData = manager.activeMaps.get('jobs-view-map');
  if (existingMapData) {
    existingMapData.markers = mapMarkers;
    existingMapData.infoWindow = mapInfoWindow;
  }
}

/* --------------------------------------------------
                Create Mapbox Jobs Map
-------------------------------------------------- */
function createMapboxJobsMap(container, manager, accessToken, jobsWithLocation, center, cacheKey, newestJobUpdatedAt) {
  // Mapbox uses [lng, lat] format for center
  const mapOptions = {
    style: 'mapbox://styles/mapbox/streets-v12',
    center: [center.lng, center.lat], // [lng, lat] format
    zoom: 6,
    cacheKey: cacheKey
  };

  // Create map with cache
  const map = manager.createMapWithCache(container, mapOptions, 'jobs-view-map', newestJobUpdatedAt);

  // Add navigation controls
  // mapboxgl is loaded from CDN and available on window
  if (window.mapboxgl && typeof window.mapboxgl.NavigationControl !== 'undefined') {
    map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
  }

  // Wait for map to load before adding markers
  map.on('load', () => {
    const mapMarkers = [];
    const mapPopups = [];

    // Loop through jobs and create markers
    jobsWithLocation.forEach((job, index) => {
      // Create marker element with pin icon
      const el = document.createElement('div');
      el.className = 'mapbox-marker';
      el.style.width = '32px';
      el.style.height = '32px';
      el.style.cursor = 'pointer';
      
      // Create pin image
      const img = document.createElement('img');
      img.src = '/map-pin.png';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.display = 'block';
      img.alt = job.name || 'Location';
      el.appendChild(img);

      // Create marker with anchor at bottom center (pin point)
      const marker = manager.createMarker({
        element: el,
        anchor: 'bottom' // Pin tip should be at the location
      });

      // Set marker position
      marker.setLngLat([parseFloat(job.location_lng), parseFloat(job.location_lat)]);
      marker.addTo(map);
      mapMarkers.push(marker);

      // Clone popup template and populate with job data
      const popupTemplate = document.getElementById('mapbox-popup-template');
      const popupElement = popupTemplate.content.cloneNode(true);
      const popupJobNameLinkElement = popupElement.querySelector('[data-js="popup-job-link"]');
      const popupJobNameElement = popupJobNameLinkElement.querySelector('[data-js="popup-job-name"]');
      const popupJobLocationElement = popupElement.querySelector('[data-js="popup-job-location"]');
      const popupJobLinkElement = popupElement.querySelector('[data-js="popup-job-link"]');

      // Populate popup with job data
      if (popupJobNameElement) popupJobNameElement.textContent = job.name || 'Unknown Project';
      if (popupJobLocationElement) popupJobLocationElement.textContent = job.location || 'Unknown Location';
      if (popupJobLinkElement) {
        popupJobLinkElement.dataset.jobUrl = '/pages/jobs/' + job.id + '/view/show';
      }

      // Convert to HTML string for popup - create a container div to get innerHTML
      const tempContainer = document.createElement('div');
      tempContainer.appendChild(popupElement);
      const popupContent = tempContainer.innerHTML;

      // Create popup with offset to keep pin visible
      // offset: [x, y] where positive y moves down, negative y moves up
      const popup = manager.createPopup({
        maxWidth: '240px',
        closeButton: false, // Disable default close button since we're using custom one
        closeOnClick: false,
        offset: [0, -15] // Move popup 15px up so pin remains visible
      }).setHTML(popupContent);

      // Add popup to map
      mapPopups.push(popup);

      // Handle marker click
      const clickHandler = () => {
        // Close all other popups
        mapPopups.forEach(p => {
          if (p.isOpen()) p.remove();
        });
        popup.setLngLat([parseFloat(job.location_lng), parseFloat(job.location_lat)]);
        popup.addTo(map);

        // Add event listener to custom close button after popup is added to DOM
        const closeButton = popup.getElement().querySelector('[data-js="popup-close"]');
        if (closeButton) {
          closeButton.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent triggering the job link click
            popup.remove();
          });
        }

        // Add event listener to job link div
        const popupJobLinkElement = popup.getElement().querySelector('[data-js="popup-job-link"]');
        if (popupJobLinkElement) {
          popupJobLinkElement.addEventListener('click', () => {
            const popupJobLinkUrl = popupJobLinkElement.dataset.jobUrl;
            if (popupJobLinkUrl) {
              window.location.href = popupJobLinkUrl;
            }
          });
        }
      };

      // Click handler for marker
      marker.getElement().addEventListener('click', clickHandler);
      marker.getElement().addEventListener('mouseenter', clickHandler);

      // Handle job card hover
      const jobCard = document.querySelector('[data-js="job-card"][data-index="' + index + '"]');
      if (jobCard) {
        jobCard.addEventListener('mouseenter', () => {
          // Pan/fly map to marker location if it's outside the viewport
          const markerPosition = [parseFloat(job.location_lng), parseFloat(job.location_lat)];
          map.flyTo({
            center: markerPosition,
            zoom: Math.max(map.getZoom(), 7), // Zoom in if zoomed out, but don't zoom out if already zoomed in
            duration: 500 // Smooth animation
          });
          
          // Close all other popups
          mapPopups.forEach(p => {
            if (p.isOpen()) p.remove();
          });
          clickHandler();
        });
      }
    });

    // Update the registered map with markers and popups
    const existingMapData = manager.activeMaps.get('jobs-view-map');
    if (existingMapData) {
      existingMapData.markers = mapMarkers;
      existingMapData.popups = mapPopups;
    }
  });
}
